import React, { useState, useRef } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Home, UserPlus, Users, Calculator, FileText, ShoppingCart,
  DollarSign, Package, PackageSearch, Settings, LogOut, Menu, X, Archive, Banknote, Database,
  ZoomIn, Moon, Sun, Inbox, Bell, Truck, Route, History,
} from "lucide-react";

const CHOFER_MENU = [
  { name: "Mis viajes", path: "/mis-viajes", icon: Truck },
  { name: "Mi historial", path: "/mi-historial", icon: History },
];

const ZOOM_LEVELS = [0.9, 1.0, 1.15];

const MENU = [
  { name: "Inicio", module: "Inicio", path: "/", icon: Home },
  { name: "Alta de clientes", module: "Alta de Clientes", path: "/alta-clientes", icon: UserPlus },
  { name: "Lista de clientes", module: "Lista de Clientes", path: "/lista-clientes", icon: Users },
  { name: "Cotizador", module: "Cotizador", path: "/cotizador", icon: Calculator },
  { name: "Cotizaciones", module: "Cotizaciones", path: "/cotizaciones", icon: FileText },
  { name: "Pedidos", module: "Pedidos", path: "/pedidos", icon: ShoppingCart },
  { name: "Historial de pedidos", module: "Historial de Pedidos", path: "/historial-pedidos", icon: Archive },
  { name: "Cobranza", module: "Cobranza", path: "/cobranza", icon: Banknote },
  { name: "Comisiones", module: "Comisiones", path: "/comisiones", icon: DollarSign },
  { name: "Avisos", module: "Avisos", path: "/avisos", icon: Bell, always: true },
  { name: "Inventario", module: "Inventario", path: "/inventario", icon: Package },
  { name: "Pedidos por surtir", module: "Pedidos por Surtir", path: "/pedidos-surtir", icon: PackageSearch },
  { name: "Viajes", path: "/viajes", icon: Route, adminOnly: true },
  { name: "Flotilla", path: "/flotilla", icon: Truck, adminOnly: true },
  { name: "DATA", module: "Data", path: "/data", icon: Database, always: true },
  { name: "Solicitudes de alta", module: "Solicitudes", path: "/solicitudes-clientes", icon: Inbox, adminOnly: true },
  { name: "Configuración del sistema", module: "Configuración", path: "/configuracion", icon: Settings },
];

export default function Layout() {
  const { user, logout, canSeeModule, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false); // móvil: menú expandido
  const [hoverPath, setHoverPath] = useState(null); // móvil: item resaltado al arrastrar
  const dragMoved = useRef(false);
  const dragTarget = useRef(null);
  const [zoomIdx, setZoomIdx] = useState(() => Number(localStorage.getItem("cs_zoom_idx") || 0));
  const [dark, setDark] = useState(() => localStorage.getItem("cs_theme") === "dark");
  const [dbOk, setDbOk] = useState(null); // null=comprobando, true=activa, false=sin conexión
  const [pendientes, setPendientes] = useState(0);
  const [avisosNoLeidos, setAvisosNoLeidos] = useState(0);

  React.useEffect(() => {
    let active = true;
    const check = () =>
      api.get("/avisos/no-leidos/count")
        .then(({ data }) => active && setAvisosNoLeidos(data.count || 0))
        .catch(() => {});
    check();
    const id = setInterval(check, 60000);
    const onRefresh = () => check();
    window.addEventListener("avisos-refresh", onRefresh);
    return () => { active = false; clearInterval(id); window.removeEventListener("avisos-refresh", onRefresh); };
  }, []);

  React.useEffect(() => {
    if (!isAdmin) return;
    let active = true;
    const check = () =>
      api.get("/solicitudes/pendientes/count")
        .then(({ data }) => active && setPendientes(data.count || 0))
        .catch(() => {});
    check();
    const id = setInterval(check, 60000);
    return () => { active = false; clearInterval(id); };
  }, [isAdmin]);

  React.useEffect(() => {
    if (!isAdmin) return;
    let active = true;
    const check = () =>
      api.get("/db/status")
        .then(({ data }) => active && setDbOk(!!data.connected))
        .catch(() => active && setDbOk(false));
    check();
    const id = setInterval(check, 60000);
    return () => { active = false; clearInterval(id); };
  }, [isAdmin]);

  React.useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("cs_theme", dark ? "dark" : "light");
  }, [dark]);

  React.useEffect(() => {
    localStorage.setItem("cs_zoom_idx", String(zoomIdx));
  }, [zoomIdx]);

  const cycleZoom = () => setZoomIdx((i) => (i + 1) % ZOOM_LEVELS.length);
  const zoom = ZOOM_LEVELS[zoomIdx] || 1;

  const esChofer = (user?.rol || "").toLowerCase() === "chofer";
  const items = esChofer ? CHOFER_MENU : MENU.filter((m) => m.always || (m.adminOnly ? isAdmin : canSeeModule(m.module)));
  const displayUser = user?.full_name || user?.username || "Usuario";

  const handleSignOut = () => {
    logout();
    navigate("/login");
  };

  // Deslizar el dedo (solo móvil), a nivel de documento para que sea confiable:
  // desde el borde izquierdo (~40px) hacia la derecha abre; deslizar a la izquierda cierra.
  React.useEffect(() => {
    let sx = null, sy = null, decided = false;
    const ts = (e) => { const t = e.touches[0]; sx = t.clientX; sy = t.clientY; decided = false; };
    const tm = (e) => {
      if (sx == null || decided) return;
      const t = e.touches[0];
      const dx = t.clientX - sx, dy = t.clientY - sy;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return; // solo swipe horizontal
      decided = true;
      if (window.innerWidth >= 768) return; // solo móvil
      if (dx > 0 && sx < 40) setOpen(true);
      else if (dx < 0) setOpen(false);
      sx = null;
    };
    document.addEventListener("touchstart", ts, { passive: true });
    document.addEventListener("touchmove", tm, { passive: true });
    return () => { document.removeEventListener("touchstart", ts); document.removeEventListener("touchmove", tm); };
  }, []);

  // Gesto móvil: al poner el dedo sobre el menú se expande; arrastras al módulo
  // y al soltar se abre ese módulo y el menú se retrae automáticamente.
  const onRailTouchStart = () => {
    if (!open) setOpen(true);
    dragMoved.current = false;
    dragTarget.current = null;
    setHoverPath(null);
  };
  const onRailTouchMove = (e) => {
    const t = e.touches[0];
    dragMoved.current = true;
    const el = document.elementFromPoint(t.clientX, t.clientY);
    const link = el && el.closest("[data-nav-path]");
    const p = link ? link.getAttribute("data-nav-path") : null;
    dragTarget.current = p;
    setHoverPath(p);
  };
  const onRailTouchEnd = (e) => {
    if (dragMoved.current && dragTarget.current) {
      e.preventDefault();
      const p = dragTarget.current;
      navigate(p);
      setOpen(false);
    }
    dragMoved.current = false;
    dragTarget.current = null;
    setHoverPath(null);
  };

  const Nav = ({ collapsed = false }) => (
    <nav className="flex-1 py-4 space-y-1 overflow-y-auto overflow-x-hidden">
      {items.map((link) => (
        <NavLink
          key={link.path}
          to={link.path}
          end={link.path === "/"}
          onClick={() => setOpen(false)}
          title={link.name}
          data-nav-path={link.path}
          data-testid={`sidebar-link-${link.path.replace(/\//g, "") || "inicio"}`}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 rounded-lg py-2 text-sm font-medium transition-all relative",
              collapsed ? "mx-2 px-0 justify-center" : "mx-2 px-3",
              isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted",
              hoverPath === link.path && "ring-2 ring-primary bg-primary/10 scale-[1.02]"
            )
          }
        >
          <link.icon className="h-5 w-5 shrink-0" />
          {!collapsed && <span className="truncate flex-1">{link.name}</span>}
          {link.path === "/solicitudes-clientes" && pendientes > 0 && (
            <span className={cn("shrink-0 text-[10px] font-bold rounded-full bg-red-500 text-white leading-tight", collapsed ? "absolute top-1 right-1 px-1" : "px-1.5 py-0.5")} data-testid="solicitudes-badge">
              {pendientes}
            </span>
          )}
          {link.path === "/avisos" && avisosNoLeidos > 0 && (
            <span className={cn("shrink-0 text-[10px] font-bold rounded-full bg-red-500 text-white leading-tight", collapsed ? "absolute top-1 right-1 px-1" : "px-1.5 py-0.5")} data-testid="avisos-badge">
              {avisosNoLeidos}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background text-foreground" style={{ zoom }} data-testid="app-shell">
      {/* Header */}
      <header className="app-header border-b bg-background fixed top-0 w-full z-20 flex items-center justify-between px-4 md:px-6">
        <div className="flex items-center gap-3">
          <button
            className="md:hidden text-foreground"
            onClick={() => setOpen((v) => !v)}
            data-testid="sidebar-toggle"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          <div className="flex items-center gap-2 font-bold text-xl tracking-tight text-primary">
            <img
              src="/logo-cooper.png"
              alt="Cooper Logo"
              className="h-8 w-auto object-contain"
            />
            <span>
              Cooper <span className="text-muted-foreground font-light">Slip ACTIVO 4</span>
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          {isAdmin && (
            <div
              className="hidden sm:flex items-center gap-1.5 text-xs rounded-full border px-2.5 py-1"
              title={dbOk === false ? "Sin conexión a la base de datos" : "Base de datos activa"}
              data-testid="db-status-badge"
            >
              <span className={cn("h-2 w-2 rounded-full", dbOk === null ? "bg-yellow-400 animate-pulse" : dbOk ? "bg-green-500" : "bg-red-500")} />
              <span className="text-muted-foreground">BD {dbOk === null ? "…" : dbOk ? "activa" : "sin conexión"}</span>
            </div>
          )}
          <div className="text-muted-foreground hidden sm:block mr-1">Hola {(user?.full_name || "").trim().split(/\s+/)[0] || user?.username}</div>
          <Button
            variant="ghost"
            size="icon"
            className="relative h-10 w-10 rounded-full"
            onClick={cycleZoom}
            title={`Zoom: ${Math.round(zoom * 100)}% (clic para cambiar)`}
            data-testid="zoom-toggle-button"
          >
            <ZoomIn className="h-5 w-5" />
            {zoomIdx > 0 && (
              <span className="absolute -bottom-0.5 -right-0.5 text-[9px] font-bold bg-primary text-primary-foreground rounded-full px-1 leading-tight">
                {zoomIdx}
              </span>
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 rounded-full"
            onClick={() => setDark((v) => !v)}
            title={dark ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
            data-testid="theme-toggle-button"
          >
            {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="user-menu-button">
                <Avatar className="h-10 w-10 border">
                  <AvatarFallback className="bg-primary/10 text-primary">
                    {displayUser.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-xs leading-none text-muted-foreground">Hasta pronto,</p>
                  <p className="text-sm font-medium leading-none">{displayUser}</p>
                  <p className="text-xs leading-none text-muted-foreground">@{user?.username}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut} className="text-red-600 cursor-pointer" data-testid="logout-button">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Cerrar sesión</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="flex app-below-header">
        {/* Sidebar desktop */}
        <aside className="hidden md:flex w-64 border-r bg-background flex-col h-screen fixed left-0 top-0 app-aside-desktop z-10">
          <Nav />
        </aside>

        {/* Sidebar móvil: riel de íconos que se expande al deslizar/tocar */}
        {open && (
          <div className="md:hidden fixed inset-x-0 bottom-0 app-overlay-top bg-black/40 z-20" onClick={() => setOpen(false)} />
        )}
        <aside
          className={cn(
            "md:hidden fixed left-0 app-aside-mobile border-r bg-background flex flex-col z-30 transition-[width] duration-200 ease-out",
            open ? "w-64 shadow-xl" : "w-16"
          )}
          onTouchStart={onRailTouchStart}
          onTouchMove={onRailTouchMove}
          onTouchEnd={onRailTouchEnd}
          data-testid="sidebar-mobile-rail"
        >
          <Nav collapsed={!open} />
        </aside>

        <main
          className="flex-1 ml-16 md:ml-64 p-4 md:p-8 min-h-[calc(100vh-4rem)] bg-muted/20"
          data-testid="main-content"
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
